/**
 * The handshake (§6.1, §15.2 #5, §15.1).
 *
 * 1. "Meeting Dana" with the partner's colour, the card timer and the hand. The hand is behind
 *    hold-to-peek (its size is fixed, so the number of cards shows nothing): hold, slide to a
 *    card and release on it to select it. Unavailable cards are greyed with the server's reason.
 * 2. "Hold to play": the chosen card's name shows only while the finger is down.
 * 3. The same "envelope sealed" screen for both players until the server says
 *    `handshakeRecorded` (it holds both views back 0.5–1.5 s after the second card), then
 *    "Handshake recorded ✓" for a fixed 3 s.
 */
import { useEffect, useState } from "react";
import { HoldButton } from "../components/HoldButton.tsx";
import { HoldToPeek } from "../components/HoldToPeek.tsx";
import { PlayerChip } from "../components/PlayerChip.tsx";
import { Countdown } from "../components/Countdown.tsx";
import { CARD_TEXT, cardLabel } from "../lib/labels.ts";
import { playCard, useGame } from "../lib/store.ts";
import { playerOf, useView } from "../lib/view.ts";
import type { CardId, CardOption } from "../lib/types.ts";

/** How long "Handshake recorded ✓" stays up (the same for everyone). */
export const RECORDED_MS = 3_000;

interface Active {
  readonly id: string;
  readonly partner: string;
}

/** Full-screen handshake overlay; renders nothing when no handshake is in progress. */
export function HandshakeFlow() {
  const view = useView();
  const current = view.currentHandshake;
  const recorded = useGame((s) => s.recorded);
  const submitted = useGame((s) => s.submitted);
  const [active, setActive] = useState<Active | null>(null);
  const [doneAt, setDoneAt] = useState<number | null>(null);

  // A new handshake appears in the view.
  if (current && active?.id !== current.id) {
    setActive({ id: current.id, partner: current.partner });
    setDoneAt(null);
  }

  // Recorded: the server's message, or (after a reconnect that missed it) the fresh view.
  const recordedInView =
    active !== null && view.handshakes.some((h) => h.id === active.id && h.recorded);
  const isRecorded = active !== null && (recorded?.handshakeId === active.id || recordedInView);
  if (isRecorded && doneAt === null && !current) {
    setDoneAt(Date.now());
  }
  // The handshake vanished without being recorded (e.g. the game moved on).
  if (active && !current && !recordedInView && !view.handshakes.some((h) => h.id === active.id)) {
    setActive(null);
  }

  useEffect(() => {
    if (doneAt === null) return;
    const t = setTimeout(
      () => {
        setActive(null);
        setDoneAt(null);
      },
      Math.max(0, doneAt + RECORDED_MS - Date.now()),
    );
    return () => clearTimeout(t);
  }, [doneAt]);

  if (!active) return null;
  const partner = playerOf(view, active.partner);

  let body;
  if (doneAt !== null) {
    body = (
      <div className="sealed" data-testid="handshake-recorded">
        <div className="envelope" aria-hidden="true">
          ✉︎
        </div>
        <h1>Handshake recorded&nbsp;✓</h1>
        <p className="muted">Nothing happens until Dusk.</p>
      </div>
    );
  } else if (current && current.myCard === null && submitted[current.id] === undefined) {
    return (
      <div className="overlay" style={{ justifyContent: "flex-start", overflow: "auto" }}>
        <ChooseCard key={current.id} />
      </div>
    );
  } else {
    body = (
      <div className="sealed" data-testid="handshake-sealed">
        <div className="envelope candles" aria-hidden="true">
          ✉︎
        </div>
        <h2>Envelope sealed</h2>
        <p className="muted">Recording the handshake…</p>
      </div>
    );
  }
  return (
    <div className="overlay" data-testid="handshake-overlay">
      <div className="stack" style={{ width: "100%", maxWidth: "30rem" }}>
        <div className="kicker">Meeting</div>
        <div className="center">
          <PlayerChip player={partner} big />
        </div>
        {body}
      </div>
    </div>
  );
}

function ChooseCard() {
  const view = useView();
  const current = view.currentHandshake!;
  const partner = playerOf(view, current.partner);
  const [selected, setSelected] = useState<CardId | null>(null);
  const [sending, setSending] = useState(false);

  const confirm = async () => {
    if (!selected || sending) return;
    setSending(true);
    const ack = await playCard(current.id, selected);
    if (!ack.ok) setSending(false);
  };

  return (
    <div className="stack" style={{ width: "100%", maxWidth: "30rem" }} data-testid="choose-card">
      <div className="row">
        <div className="grow" style={{ textAlign: "left" }}>
          <div className="kicker">Meeting</div>
          <PlayerChip player={partner} big />
        </div>
        <Countdown deadline={current.deadline} testId="card-timer" />
      </div>
      <CardHand options={current.options} selected={selected} onSelect={(c) => setSelected(c)} />
      <p className="muted" style={{ margin: 0 }} data-testid="card-status">
        {selected ? "A card is chosen. Hold the button to play it." : "No card chosen yet."}
      </p>
      <HoldButton
        testId="play-card"
        disabled={!selected || sending}
        onConfirm={() => void confirm()}
        whileHolding={selected ? `Playing ${cardLabel(selected)}…` : undefined}
      >
        Hold to play
      </HoldButton>
      <p className="faint center">If the timer runs out, you play Chat.</p>
    </div>
  );
}

/**
 * The card hand behind hold-to-peek. Hold, slide to a card, release on it to select it. The
 * veiled box has a fixed height whatever the number of cards.
 *
 * Keyboard: hold Space to see the hand, move with the arrow keys, and release Space (or press
 * Enter) to choose the highlighted card.
 */
export function CardHand({
  options,
  selected,
  onSelect,
}: {
  options: readonly CardOption[];
  selected: CardId | null;
  onSelect: (card: CardId) => void;
}) {
  const [cursor, setCursor] = useState<number | null>(null);
  const move = (key: string) => {
    const avail = options.map((o, i) => (o.available ? i : -1)).filter((i) => i >= 0);
    if (avail.length === 0) return;
    if (key === "Enter") {
      const o = cursor !== null ? options[cursor] : undefined;
      if (o?.available) onSelect(o.card);
      return;
    }
    const back = key === "ArrowUp" || key === "ArrowLeft";
    const at = cursor === null ? -1 : avail.indexOf(cursor);
    const next =
      at < 0 ? (back ? avail.length - 1 : 0) : (at + (back ? avail.length - 1 : 1)) % avail.length;
    setCursor(avail[next] ?? null);
  };
  const pick = (point: { x: number; y: number } | null) => {
    if (!point) {
      const o = cursor !== null ? options[cursor] : undefined;
      if (o?.available) onSelect(o.card);
      setCursor(null);
      return;
    }
    if (typeof document.elementFromPoint !== "function") return;
    const el = document.elementFromPoint(point.x, point.y)?.closest<HTMLElement>("[data-card]");
    const card = el?.dataset.card as CardId | undefined;
    const option = options.find((o) => o.card === card);
    if (option?.available) onSelect(option.card);
  };
  return (
    <HoldToPeek
      label="Hold to see your cards"
      hint="Slide to a card and release on it to choose"
      minHeight="22rem"
      testId="hand-peek"
      onRelease={pick}
      onShownKey={move}
    >
      <CardList options={options} selected={selected} cursor={cursor} />
    </HoldToPeek>
  );
}

export function CardList({
  options,
  selected,
  cursor = null,
}: {
  options: readonly CardOption[];
  selected: CardId | null;
  /** Keyboard highlight (index into options). */
  cursor?: number | null;
}) {
  return (
    <div className="hand" data-testid="card-list">
      {options.map((o, i) => (
        <div
          key={o.card}
          data-cursor={cursor === i ? "true" : undefined}
          data-card={o.card}
          data-available={o.available ? "true" : "false"}
          className={`card ${o.available ? "" : "unavailable"} ${selected === o.card ? "selected" : ""} ${cursor === i ? "cursor" : ""}`}
        >
          <div className="card-name">
            <span>{cardLabel(o.card)}</span>
            {o.remaining !== null ? <span className="faint">{o.remaining} left</span> : null}
          </div>
          {o.available ? (
            <div className="card-text">{CARD_TEXT[o.card]}</div>
          ) : (
            <div className="reason">{o.reason}</div>
          )}
          {o.warning ? <div className="warning">⚠ {o.warning}</div> : null}
        </div>
      ))}
    </div>
  );
}
